import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'terms' | 'privacy' | null;
function Modal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return <AnimatePresence data-id="element-85">
      {isOpen && <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} data-id="element-86">
          <div className="absolute inset-0 bg-warm-dark/60 backdrop-blur-sm" onClick={onClose} data-id="element-87" />
          <motion.div className="relative bg-warm-bg rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden" initial={{
        scale: 0.95,
        opacity: 0,
        y: 20
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} exit={{
        scale: 0.95,
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.25,
        ease: [0.21, 0.47, 0.32, 0.98]
      }} data-id="element-88">
            <div className="flex items-center justify-between p-6 border-b border-warm-alt" data-id="element-89">
              <h2 className="text-xl font-heading font-medium text-warm-dark" data-id="element-90">
                {title}
              </h2>
              <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-warm-alt transition-colors text-warm-dark/60 hover:text-warm-dark" data-id="element-91">
                <XIcon className="w-5 h-5" data-id="element-92" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)] text-warm-dark/80 text-sm leading-relaxed space-y-4" data-id="element-93">
              {children}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-warm-dark text-warm-bg/60 py-12 border-t border-white/10" data-id="element-94">
        <div className="max-w-6xl mx-auto px-6" data-id="element-95">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6 mb-8" data-id="element-96">
            <img src="https://cdn.magicpatterns.com/uploads/8XezKwaD9LGnpzQL9aUbLz/FEB522F3-9C04-4F3B-A005-C6C7F980373E.png" alt="Espalda Libre" className="h-20 md:h-24 w-auto object-contain brightness-0 invert opacity-90" data-id="element-97" />
            <div className="flex gap-6 text-sm" data-id="element-98">
              <button onClick={() => setActiveModal('terms')} className="hover:text-warm-bg transition-colors cursor-pointer" data-id="element-99">
                Términos y Condiciones
              </button>
              <button onClick={() => setActiveModal('privacy')} className="hover:text-warm-bg transition-colors cursor-pointer" data-id="element-100">
                Política de Privacidad
              </button>
            </div>
          </div>

          <div className="text-xs text-warm-bg/40 text-center md:text-left space-y-4 max-w-4xl" data-id="element-101">
            <p data-id="element-102">
              <strong data-id="element-103">Aviso Responsable:</strong> El contenido de este ebook y
              de esta página web es estrictamente educativo e informativo,
              basado en la experiencia personal de la autora. No constituye
              consejo médico, diagnóstico ni tratamiento. Siempre debes
              consultar con un médico, fisioterapeuta o profesional de la salud
              cualificado antes de comenzar cualquier programa de ejercicios o
              realizar cambios en tu estilo de vida, especialmente si sufres de
              dolor crónico o condiciones médicas preexistentes.
            </p>
            <p data-id="element-104">
              © {new Date().getFullYear()} Espalda Libre. Todos los derechos
              reservados.
            </p>
          </div>
        </div>
      </footer>

      {/* Terms & Conditions Modal */}
      <Modal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Términos y Condiciones" data-id="element-105">
        <p data-id="element-106">
          <strong data-id="element-107">1. Información General</strong>
        </p>
        <p data-id="element-108">
          Al adquirir el ebook "Espalda Libre", aceptas los siguientes términos
          y condiciones. Este producto es un ebook digital descargable destinado
          exclusivamente a fines educativos e informativos.
        </p>

        <p data-id="element-109">
          <strong data-id="element-110">2. Naturaleza del Producto</strong>
        </p>
        <p data-id="element-111">
          "Espalda Libre" es una guía basada en la experiencia personal de la
          autora. No constituye asesoramiento médico, diagnóstico ni tratamiento
          profesional. El contenido no debe utilizarse como sustituto de la
          consulta con profesionales de la salud cualificados.
        </p>

        <p data-id="element-112">
          <strong data-id="element-113">3. Uso del Producto</strong>
        </p>
        <p data-id="element-114">
          El ebook es para uso personal e intransferible. Queda prohibida su
          reproducción, distribución, modificación o reventa total o parcial sin
          autorización expresa de la autora.
        </p>

        <p data-id="element-115">
          <strong data-id="element-116">4. Política de Reembolso</strong>
        </p>
        <p data-id="element-117">
          Debido a la naturaleza digital del producto, una vez realizada la
          descarga no se aceptan devoluciones. Si tienes algún problema técnico
          con la descarga, contáctanos y te ayudaremos a resolverlo.
        </p>

        <p data-id="element-118">
          <strong data-id="element-119">5. Limitación de Responsabilidad</strong>
        </p>
        <p data-id="element-120">
          La autora no se hace responsable de los resultados individuales
          obtenidos tras la lectura del ebook. Cada persona es diferente y los
          resultados pueden variar. Siempre consulta con un profesional de la
          salud antes de realizar cambios en tu rutina de ejercicios o estilo de
          vida.
        </p>

        <p data-id="element-121">
          <strong data-id="element-122">6. Propiedad Intelectual</strong>
        </p>
        <p data-id="element-123">
          Todo el contenido del ebook, incluyendo textos, diseño y estructura,
          está protegido por derechos de autor. Cualquier uso no autorizado será
          perseguido conforme a la legislación vigente.
        </p>

        <p data-id="element-124">
          <strong data-id="element-125">7. Modificaciones</strong>
        </p>
        <p data-id="element-126">
          Nos reservamos el derecho de modificar estos términos en cualquier
          momento. Las modificaciones serán efectivas desde su publicación en
          esta página.
        </p>
      </Modal>

      {/* Privacy Policy Modal */}
      <Modal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Política de Privacidad" data-id="element-127">
        <p data-id="element-128">
          <strong data-id="element-129">1. Datos que Recopilamos</strong>
        </p>
        <p data-id="element-130">
          Al realizar tu compra, recopilamos únicamente los datos necesarios
          para procesar la transacción: nombre, dirección de correo electrónico
          y datos de pago. No almacenamos información de tarjetas de crédito
          directamente.
        </p>

        <p data-id="element-131">
          <strong data-id="element-132">2. Uso de la Información</strong>
        </p>
        <p data-id="element-133">Utilizamos tu información exclusivamente para:</p>
        <ul className="list-disc pl-5 space-y-1" data-id="element-134">
          <li data-id="element-135">Procesar y entregar tu compra</li>
          <li data-id="element-136">Enviarte el enlace de descarga del ebook</li>
          <li data-id="element-137">Comunicarnos contigo en caso de problemas con tu pedido</li>
        </ul>

        <p data-id="element-138">
          <strong data-id="element-139">3. Protección de Datos</strong>
        </p>
        <p data-id="element-140">
          Implementamos medidas de seguridad técnicas y organizativas para
          proteger tu información personal contra acceso no autorizado, pérdida
          o alteración.
        </p>

        <p data-id="element-141">
          <strong data-id="element-142">4. Compartir Información</strong>
        </p>
        <p data-id="element-143">
          No vendemos, alquilamos ni compartimos tu información personal con
          terceros, excepto los proveedores de servicios necesarios para
          procesar tu pago y entrega del producto.
        </p>

        <p data-id="element-144">
          <strong data-id="element-145">5. Cookies</strong>
        </p>
        <p data-id="element-146">
          Esta página puede utilizar cookies para mejorar tu experiencia de
          navegación y analizar el tráfico del sitio. Puedes configurar tu
          navegador para rechazar cookies si lo deseas.
        </p>

        <p data-id="element-147">
          <strong data-id="element-148">6. Tus Derechos</strong>
        </p>
        <p data-id="element-149">
          Tienes derecho a acceder, rectificar o eliminar tus datos personales
          en cualquier momento. Para ejercer estos derechos, puedes contactarnos
          a través de los canales disponibles.
        </p>

        <p data-id="element-150">
          <strong data-id="element-151">7. Cambios en esta Política</strong>
        </p>
        <p data-id="element-152">
          Nos reservamos el derecho de actualizar esta política de privacidad.
          Cualquier cambio será publicado en esta página con la fecha de última
          actualización.
        </p>

        <p className="text-warm-dark/50 text-xs mt-4" data-id="element-153">
          Última actualización: Abril 2026
        </p>
      </Modal>
    </>;
}